import { Navigate, Outlet } from 'react-router-dom'
import Storage from '../../../storage/auth'
import { LOGIN } from 'navigation/CONSTANTS'
import BaseScreen from 'pages/base'
import FirebaseListenerProvider from 'services/context/firebaseListener'
import { firebaseSignInWithCustomToken } from 'services/auth'
import { useEffect, useState } from 'react'
import { getAuth, Unsubscribe } from 'firebase/auth'
import { Box, CircularProgress } from '@mui/material'
import PreventNavigationProvider from 'navigation/preventNavigation'
import { session } from 'services/user'
import { useAppDispatch } from 'reduxStore/store'
import { setInitialEstablishment } from 'reduxStore/slices'
import AcceptTerms from 'pages/AcceptTerms'
import { isTermsAcceptancePending } from 'pages/AcceptTerms/constants'

/**
 * PrivateRoute component
 * This component checks if the user is authenticated before allowing access to the routes.
 * If the user is not authenticated, it redirects to the login page.
 * If the user is authenticated, it renders the children components wrapped in a BaseScreen.
 * @param param0 - redirectPath: Path to redirect if the user is not authenticated
 * @returns JSX.Element
 */
export const PrivateRoute = ({ redirectPath = LOGIN }) => {
  const [state, setState] = useState<
    'loading' | 'unauthenticated' | 'authenticated'
  >('loading')
  /** Whether the authenticated professional still needs to accept the terms */
  const [termsRequired, setTermsRequired] = useState(false)
  /** Dispatch function to update the Redux store */
  const dispatch = useAppDispatch()

  /** Effect to fetch session and set authentication state
   * This effect checks if the user is authenticated and updates the state accordingly
   */
  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null

    /** Function to fetch session and update authentication state */
    const fetchSession = async () => {
      const response = await session()
      if (
        response.success &&
        response.data &&
        response.data.user_type !== 'patient'
      ) {
        Storage.user.set(response.data)
        setTermsRequired(isTermsAcceptancePending(response.data))
        const sessionInfo = response.data.session_info
        if (sessionInfo) {
          dispatch(
            setInitialEstablishment(sessionInfo.current_establishment_id)
          )
        }
        const auth = getAuth()
        unsubscribe = auth.onAuthStateChanged(authState => {
          if (!authState) {
            firebaseSignInWithCustomToken(Storage.customToken.get() ?? '').catch(
              error => {
                console.error('Failed to re-authenticate with Firebase:', error)
                setState('unauthenticated')
              }
            )
          }
          setState(authState ? 'authenticated' : 'unauthenticated')
        })
      } else {
        setState('unauthenticated')
      }
    }

    fetchSession()

    return () => {
      if (unsubscribe) {
        unsubscribe()
      }
    }
  }, [])

  const render = {
    loading: (
      <Box
        sx={{
          width: '100%',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: 'GrayText'
          }}
        />
      </Box>
    ),
    unauthenticated: <Navigate to={redirectPath} />,
    authenticated: termsRequired ? (
      <AcceptTerms onAccepted={() => setTermsRequired(false)} />
    ) : (
      <PreventNavigationProvider>
        <FirebaseListenerProvider>
          <BaseScreen>
            <Outlet />
          </BaseScreen>
        </FirebaseListenerProvider>
      </PreventNavigationProvider>
    )
  }

  return render[state]
}

export default PrivateRoute
